import Grid from "@mui/material/Grid/Grid";
import {ColDef} from "ag-grid-community/dist/lib/entities/colDef";
import {AgGridReact} from "ag-grid-react/lib/agGridReact";
import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {useRecoilState} from "recoil";
import {useAgGridLocale} from "../../../../../../config/globalConfig";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import {usePeakDetailRepository} from "../../../../api/repositories/PeakDetailRepository";
import {PeakAssociationRequest, PeakDetail, PeakMatrixRecord} from "../../../../api/types/peakDetail";
import {ProcessCellForExportParams} from "ag-grid-community";
import {peakCreationAtom} from "../../../../state/peaksRecoil";
import {useMatrixColDefs} from "./useMatrixColDefs";
import {GridApi} from "ag-grid-enterprise";
import {BASE_DEFINITION_FORM_STEPS, DEFAULT_MATRIX_COL_DEF} from "../../../../constants";
import PercentageCellEditor from "../../../../common/components/PercentageCellEditor";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import PeakDaySelectEditor from "./PeakDaySelectEditor";

const PeakMatrixDefinition = ({submitRef}: { submitRef: RefObject<HTMLButtonElement> }) => {
    const {t} = useTranslation(['pricing']);
    // Use State
    const [rowData, setRowData] = useState<{ [key: string]: number | string | null | PeakDetail }[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    // Use Repository Hooks
    const {updatePeakMatrix} = usePeakDetailRepository();

    // use Recoil
    const [peakCreation, setPeakCreation] = useRecoilState(peakCreationAtom);
    const {peakId, readOnly, matrixData, newId} = peakCreation;

    // Use Ref
    const gridRef = useRef<AgGridReact<PeakMatrixRecord>>(null);

    // agGrid
    const columnDefs: ColDef[] = useMatrixColDefs({
        matrixData: matrixData as PeakDetail[],
        editable: !readOnly
    });
    const agGridLocale = useAgGridLocale();

    const matrixInit = useCallback(() => {
        if (matrixData?.length) {

            const rowData: PeakDetail[] = matrixData || [];

            setRowData(JSON.parse(JSON.stringify(rowData)));
        }
    }, [matrixData]);

    useEffect(() => {
        matrixInit();
    }, [matrixInit]);

    const i18nDays: { [key: string]: number } = {
        [t('days.monday')]: 1,
        [t('days.tuesday')]: 2,
        [t('days.wednesday')]: 3,
        [t('days.thursday')]: 4,
        [t('days.friday')]: 5,
        [t('days.saturday')]: 6,
        [t('days.sunday')]: 0,
        [t('days.none')]: -1,
    }

    const matrixConfirm = async () => {
        if (newId || peakId) {
            const request: PeakAssociationRequest = {
                peakRateId: newId ? newId as number : peakId as number,
                peakRateDetails: []
            };

            if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node) => {
                if (node.data) {
                    const row: PeakDetail = node.data;
                    const i18nDaysEn: { [key: string]: number } = {
                        "MONDAY": 1,
                        "TUESDAY": 2,
                        "WEDNESDAY": 3,
                        "THURSDAY": 4,
                        "FRIDAY": 5,
                        "SATURDAY": 6,
                        "SUNDAY": 0,
                    }
                    //TODO SOSTITUIRE LA Ì CON IL CODIC ASCII
                    const i18nDaysIt: { [key: string]: number } = {
                        "LUNEDÌ": 1,
                        "MARTEDÌ": 2,
                        "MERCOLEDÌ": 3,
                        "GIOVEDÌ": 4,
                        "VENERDÌ": 5,
                        "SABATO": 6,
                        "DOMENICA": 0,
                    }
                    console.log(i18nDaysIt[row.peakDay]);
                    console.log(row);
                    //CHECK SAVE PEAK AND PEAK OFF DAY
                    let savePeakDay = "undefined";
                    let savePeakOffDay = "undefined";
                    if (i18nDays[row.peakDay] !== undefined && i18nDays[row.peakDay] !== -1) {
                        let checkPeakDay = dayjs().day(i18nDays[row.peakDay]);
                        savePeakDay = dayjs(checkPeakDay).locale("en").format("dddd").toUpperCase();
                    }
                    if (i18nDays[row.peakOffDay] !== undefined && i18nDays[row.peakOffDay] !== -1) {
                        let checkPeakOffDay = dayjs().day(i18nDays[row.peakOffDay]);
                        savePeakOffDay = dayjs(checkPeakOffDay).locale("en").format("dddd").toUpperCase();
                    }

                    request.peakRateDetails.push({
                        ...row,
                        peakDay: savePeakDay,
                        peakOffDay: savePeakOffDay,
                    });
                }
            });

            try {
                await updatePeakMatrix(request);
                setPeakCreation({...peakCreation, step: BASE_DEFINITION_FORM_STEPS.DONE});
            } catch (e) {
            }
        }
    };

    const defaultColDef = {...DEFAULT_MATRIX_COL_DEF};

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value) && params.column.colId.includes("peakValue")) {
            return params.value;
            // @ts-ignore
        } else if (!isNaN(params.value) && params.column.colId.includes("peakOffValue")) {
            return params.value;
            // @ts-ignore
        } else if (isNaN(params.value) && params.column.colId.includes("peakDay")) {
            return params.value;
            // @ts-ignore
        } else if (isNaN(params.value) && params.column.colId.includes("peakOffDay")) {
            return params.value;
        } else {
            return "";
        }
    }

    return (
        <Grid container spacing={2} p={2}>
            <Grid item xs={12} sm={12}>
                <AgGridCustomTable
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    domLayout={"autoHeight"}
                    tooltipShowDelay={0}
                    columnDefs={columnDefs}
                    gridRef={gridRef}
                    localeText={agGridLocale}
                    pagination={false}
                    onGridReady={onGridReady}
                    frameworkComponents={{
                        customEditor: PercentageCellEditor,
                        peakDaySelectEditor: PeakDaySelectEditor,
                    }}
                    rowSelection={"multiple"}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    enableCellTextSelection={false}
                    processCellFromClipboard={processCellFromClipboard}
                />
            </Grid>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    matrixConfirm();
                }}
            >
                <button ref={submitRef} type="submit" style={{display: "none"}}/>
            </form>
        </Grid>
    );
};

export default PeakMatrixDefinition;
